import EVImg from '@components/EVImg/EVImg';
import ConditionallyWrapWithUrl from '@components/MiscComponents/ConditionallyWrapWithUrl';
import { FavoriteButton } from '@components/MiscComponents/FavoriteButton';
import { PropertySubTypeEnum, PropertyTypeEnum } from '@engelvoelkersamericas/api';
import { useCurrency } from '@util/customHooks/useCurrency';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import usePropertySaver from '@util/customHooks/usePropertySaver';
import { processListingStatus } from '@util/listings';
import { tPropertySubType, tPropertyType } from 'i18n/translation-utils';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import { FC, useMemo } from 'react';
import { CardImage } from '../BaseCard/BaseCard';
import {
  CardContentWrapper,
  CardHeaderStyled,
  CardHeaderWrapper,
  CardWrapper,
  EmptyCardImage,
} from '../style';
import PropertyListingImageCarousel from './PropertyListingImageCarousel';
import {
  ContactDetailList,
  LinksWrapper,
  LinkText,
  PropertyCooperativeAgencyStatus,
  PropertyListingHeaderWrapper,
  PropertyListingStatusWrapper,
  StyledCardContentDetailList,
  StyledLogoWrapper,
} from './style';

const BAHAMAS_SHOP = 'Engel & Völkers Bahamas';

interface PropertyListingCardProps {
  id: string;
  imgs: string[];
  listOfficeMlsId: string;
  listPrice: number;
  closePrice?: number | null;
  closePriceToDisplay?: string;
  listUrl: string;
  listAgentFullName?: string;
  buyerAgentFullName?: string;
  buyerOfficeName?: string;
  attributionContact?: string;
  openHouse?: any[];
  listingStatus: string;
  propertySubType: string;
  propertyType: string;
  address: string;
  bathrooms: string;
  bedrooms: string;
  squareFootage: string;
  acreage: string;
  listOfficeName?: string;
  listOfficeUrl?: string;
  className?: string;
  originatingSystemName: string;
  isStarIconVisible?: boolean;
  refreshProperties?: () => void;
  custom14?: string;
  custom15?: string;
  country: string;
  currencyCode: string;
  isCooperativeAgency?: boolean;
  cooperativeAgencyName?: string;
}

const PropertyListingCard: FC<PropertyListingCardProps> = observer(
  ({
    id,
    imgs,
    listOfficeMlsId,
    listPrice,
    listUrl,
    listAgentFullName,
    buyerAgentFullName,
    buyerOfficeName,
    attributionContact,
    openHouse,
    listingStatus,
    propertySubType,
    propertyType,
    address,
    bathrooms,
    bedrooms,
    squareFootage,
    acreage,
    listOfficeName,
    listOfficeUrl,
    className,
    originatingSystemName,
    isStarIconVisible = true,
    refreshProperties,
    closePrice,
    closePriceToDisplay,
    custom14,
    custom15,
    country,
    currencyCode,
    isCooperativeAgency = false,
    cooperativeAgencyName,
  }) => {
    const t = useTranslations();
    const { appStore } = useStores();
    const { mlsComplianceData } = appStore;
    const { getListingPrice } = useCurrency();
    const { convertArea } = useMeasurement();
    const { isFav, onSaveProperty } = usePropertySaver({ listingId: id, listOfficeMlsId });

    // process listing status
    const listingStatusProcessed = useMemo(() => {
      return processListingStatus(openHouse ?? null, listingStatus, propertyType);
    }, [openHouse, listingStatus]);

    const { formattedPrice } = getListingPrice({
      standardStatus: listingStatus,
      originatingSystemName: originatingSystemName,
      listPrice: listPrice!,
      closePrice: closePrice,
      closePriceToDisplay: closePriceToDisplay,
      country: country,
      listingCurrency: currencyCode,
      custom14,
      custom15,
    });

    const osnComplianceData = mlsComplianceData?.[originatingSystemName ?? ''];

    const getListingOfficeName = () => {
      if (isCooperativeAgency && cooperativeAgencyName) {
        return cooperativeAgencyName;
      }
      return listOfficeName!.replace('V?lkers', 'Völkers');
    };

    const isOfficeNameValid = (isCooperativeAgency && cooperativeAgencyName) || listOfficeName;

    const complianceText = isOfficeNameValid && (
      <>
        <>{t('Listing Courtesy of')} </>
        {listOfficeUrl === '' ? (
          <a href={listOfficeUrl}>{getListingOfficeName()}</a>
        ) : (
          <>{getListingOfficeName()}</>
        )}
      </>
    );

    const nerenText = (
      <>
        {listAgentFullName && `, ${listAgentFullName}`}
        {attributionContact && `, ${attributionContact}`}
        <br />
        {buyerAgentFullName && `Sold by ${buyerAgentFullName}`}
        {buyerOfficeName && `, ${buyerOfficeName}`}
      </>
    );

    return (
      <CardWrapper className={className}>
        {imgs && Array.isArray(imgs) && imgs.length > 0 ? ( //first check if array exists and has length
          <ConditionallyWrapWithUrl href={listUrl}>
            {imgs.length === 1 ? ( //render single if imgs array is only one element
              <CardImage image={imgs[0]!} alt_text="property" imageAspectRatio={0.75} />
            ) : (
              // otherwise render carousel
              <PropertyListingImageCarousel imgs={imgs} />
            )}
          </ConditionallyWrapWithUrl>
        ) : (
          <ConditionallyWrapWithUrl href={listUrl}>
            <EmptyCardImage />
          </ConditionallyWrapWithUrl>
        )}

        <PropertyListingHeaderWrapper>
          {formattedPrice && (
            <CardHeaderWrapper>
              <ConditionallyWrapWithUrl href={listUrl}>
                <CardHeaderStyled>{`${formattedPrice}`}</CardHeaderStyled>
              </ConditionallyWrapWithUrl>

              {isStarIconVisible && (
                <FavoriteButton
                  $isFavorite={isFav}
                  onClick={() => onSaveProperty(refreshProperties)}
                />
              )}
            </CardHeaderWrapper>
          )}
        </PropertyListingHeaderWrapper>
        <CardContentWrapper>
          {listingStatusProcessed && (
            <PropertyListingStatusWrapper>{listingStatusProcessed}</PropertyListingStatusWrapper>
          )}
          {isCooperativeAgency && (
            <PropertyCooperativeAgencyStatus>
              {t('Cooperative Agency')}
            </PropertyCooperativeAgencyStatus>
          )}

          <StyledCardContentDetailList>
            {address && <li> {address} </li>}
            <li>
              <ContactDetailList>
                {propertyType === 'Land' || !propertySubType ? (
                  <li>{tPropertyType(propertyType as PropertyTypeEnum)}</li>
                ) : null}
                {!!propertySubType && propertyType !== 'Land' ? (
                  <li>{tPropertySubType(propertySubType as PropertySubTypeEnum)}</li>
                ) : null}
                {bedrooms && (
                  <li data-testid="property-listing-bedrooms">
                    {bedrooms} {t('Beds')}
                  </li>
                )}
                {bathrooms && (
                  <li data-testid="property-listing-bathrooms">
                    {bathrooms} {t('Baths')}
                  </li>
                )}
                {propertyType === 'Land' && acreage ? (
                  <li data-testid="property-listing-size">
                    {acreage} {t('Acres')}
                  </li>
                ) : squareFootage ? (
                  <li>
                    {
                      convertArea(parseFloat(String(squareFootage).replace(/,/g, '')), 'sq.ft')
                        .formattedArea
                    }
                  </li>
                ) : (
                  <></>
                )}
              </ContactDetailList>
            </li>
          </StyledCardContentDetailList>

          {/* we want only an explicit `false` to hide this, `undefined` should show it */}
          {/* never show compliance text for the Bahamas shop */}
          {osnComplianceData &&
            BAHAMAS_SHOP !== listOfficeName &&
            originatingSystemName &&
            osnComplianceData?.includeListingCourtesyOf !== false && (
              <LinksWrapper>
                <span>
                  <LinkText>
                    {complianceText}
                    {originatingSystemName === 'NEREN' ? nerenText : null}
                  </LinkText>
                </span>
                {!(isCooperativeAgency && cooperativeAgencyName) &&
                  osnComplianceData.img?.fields.file?.url && (
                    <>
                      <StyledLogoWrapper>
                        <EVImg
                          src={osnComplianceData.img?.fields.file?.url}
                          imageDesc={
                            osnComplianceData.name ? osnComplianceData.name : 'Compliance Logo'
                          }
                          priority={true}
                          layout="fill"
                          objectFit="contain"
                          unoptimized={true}
                        />
                      </StyledLogoWrapper>
                    </>
                  )}
              </LinksWrapper>
            )}
        </CardContentWrapper>
      </CardWrapper>
    );
  },
);

export default PropertyListingCard;
